import React, { useState, useEffect, useMemo } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
  TruckIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  BuildingOffice2Icon,
  CurrencyDollarIcon,
  ArchiveBoxIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline';
import { downloadInvoicePDF } from '../../utils/pdfService';
import PurchaseForm from '../../components/purchases/PurchaseForm';
import PurchaseDetail from '../../components/purchases/PurchaseDetail';
import { useAppData } from '../../contexts/AppDataContext';
import { transformPurchasesData, calculatePurchasesStats } from '../../utils/purchasesDataUtils';
import BarcodeScanner from '../../components/common/BarcodeScanner';

const PurchasesPage = () => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [purchasesPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');
  const navigate = useNavigate();
  const location = useLocation();
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const { getPurchases, getSuppliers } = useAppData();

  const getSupplierName = (s) => {
    if (!s) return '';
    return s.name || '';
  };
  const formatAmount = (num) => `${Number(num || 0).toLocaleString()} DA`;
  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch { return iso; }
  };

  useEffect(() => {
    // Vérifier si l'URL contient le paramètre scan=true
    const params = new URLSearchParams(location.search);
    setIsScannerOpen(params.get('scan') === 'true');
  }, [location.search]);

  // Gérer le téléchargement du PDF
  const handleDownloadPDF = (purchase) => {
    try {
      const mockInvoiceData = {
        invoiceNumber: purchase.purchaseNumber || purchase.id,
        issueDate: purchase.date,
        items: purchase.items || [{
          description: 'Produit acheté',
          quantity: 1,
          price: parseFloat(purchase.amount.replace(/[^0-9.-]+/g,'')),
          total: parseFloat(purchase.amount.replace(/[^0-9.-]+/g,''))
        }],
        subtotal: parseFloat(purchase.amount.replace(/[^0-9.-]+/g,'')),
        discount: 0,
        tax: 0,
        total: parseFloat(purchase.amount.replace(/[^0-9.-]+/g,'')),
      };

      const mockSupplierData = {
        name: purchase.supplier,
        email: 'supplier@example.com',
        phone: '+213 XXX XXX XXX',
        address: 'Adresse du fournisseur',
      };

      downloadInvoicePDF(mockInvoiceData, mockSupplierData);
      toast.success('Téléchargement du PDF démarré');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
      console.error(error);
    }
  };

  // Gérer l'ouverture du scanner
  const openScanner = () => {
    navigate('?scan=true');
  };

  // Gérer la fermeture du scanner
  const closeScanner = () => {
    navigate(location.pathname);
  };

  // Gérer le scan d'un code-barres
  const handleScan = (barcode) => {
    toast.success(`Code-barres scanné: ${barcode}`);

    // Vérifier si le code-barres correspond à un produit dans le système
    // Simuler une recherche de produit
    setTimeout(() => {
      // Rediriger vers la page de création d'achat avec le produit scanné
      navigate(`/purchases/new?product=${barcode}`);
    }, 500);
  };

  // Gérer les erreurs de scan
  const handleScanError = (error) => {
    toast.error(`Erreur de scan: ${error}`);
  };

  // Gérer les actions
  const handleView = (purchase) => {
    const payload = purchase.__raw ? purchase.__raw : purchase;
    navigate(`/purchases/${purchase.id}`, { state: { purchase: payload } });
  };

  const handleEdit = (purchase) => {
    navigate(`/purchases/${purchase.id}/edit`);
  };

  const handleDelete = (purchaseId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet achat ?')) {
      // Ici, dans une application réelle, on appellerait une API pour supprimer l'achat
      toast.success('Achat supprimé avec succès');
      // Si nous avions un état local des achats, nous le mettrions à jour ici
    }
  };

  // Gérer la réception de marchandises par scanner
  const handleReceiveItems = () => {
    openScanner();
    toast.success('Scanner les produits à réceptionner');
  };

  // Données simulées pour la démonstration
  const demoPurchases = [
    { id: 'P001', purchaseNumber: 'ACH-2023-001', supplier: 'Fournisseur A', date: '2023-07-15', amount: '125 000 DA', status: 'received', type: 'products' },
    { id: 'P002', purchaseNumber: 'ACH-2023-002', supplier: 'Fournisseur B', date: '2023-07-18', amount: '78 500 DA', status: 'ordered', type: 'products' },
    { id: 'P003', purchaseNumber: 'ACH-2023-003', supplier: 'Fournisseur C', date: '2023-07-20', amount: '45 000 DA', status: 'partial', type: 'equipment' },
    { id: 'P004', purchaseNumber: 'ACH-2023-004', supplier: 'Fournisseur A', date: '2023-07-22', amount: '32 000 DA', status: 'received', type: 'products' },
    { id: 'P005', purchaseNumber: 'ACH-2023-005', supplier: 'Fournisseur D', date: '2023-07-25', amount: '18 750 DA', status: 'cancelled', type: 'services' },
    { id: 'P006', purchaseNumber: 'ACH-2023-006', supplier: 'Fournisseur B', date: '2023-07-28', amount: '95 000 DA', status: 'ordered', type: 'products' },
    { id: 'P007', purchaseNumber: 'ACH-2023-007', supplier: 'Fournisseur E', date: '2023-08-01', amount: '63 500 DA', status: 'received', type: 'equipment' },
    { id: 'P008', purchaseNumber: 'ACH-2023-008', supplier: 'Fournisseur A', date: '2023-08-03', amount: '42 000 DA', status: 'partial', type: 'products' },
    { id: 'P009', purchaseNumber: 'ACH-2023-009', supplier: 'Fournisseur C', date: '2023-08-05', amount: '28 750 DA', status: 'received', type: 'services' },
    { id: 'P010', purchaseNumber: 'ACH-2023-010', supplier: 'Fournisseur D', date: '2023-08-08', amount: '105 000 DA', status: 'ordered', type: 'products' },
    { id: 'P011', purchaseNumber: 'ACH-2023-011', supplier: 'Fournisseur B', date: '2023-08-10', amount: '72 500 DA', status: 'received', type: 'equipment' },
    { id: 'P012', purchaseNumber: 'ACH-2023-012', supplier: 'Fournisseur E', date: '2023-08-12', amount: '54 000 DA', status: 'cancelled', type: 'products' },
  ];

  // Real purchases from context
  const suppliers = useMemo(() => getSuppliers(), [getSuppliers]);
  const purchasesFromContext = useMemo(() => getPurchases(), [getPurchases]);

  const realPurchasesDisplay = useMemo(() => {
    console.log('🔄 PurchasesPage: Using shared transformation utility');
    // Use the SHARED transformation utility - ensures consistency with PurchasesReports
    return transformPurchasesData(purchasesFromContext || [], suppliers);
  }, [purchasesFromContext, suppliers]);

  const allPurchases = [...realPurchasesDisplay, ...demoPurchases];

  // Filtrer les achats en fonction de l'onglet actif et du terme de recherche
  const purchases = allPurchases.filter(purchase => {
    // Filtrer par onglet (statut uniquement)
    if (activeTab !== 'all' && purchase.status !== activeTab) {
      return false;
    }
    
    // Filtrer par terme de recherche
    if (searchTerm && !purchase.purchaseNumber.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !purchase.supplier.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Filtrer par statut
    if (statusFilter !== 'all' && purchase.status !== statusFilter) {
      return false;
    }
    
    return true;
  });

  // Calculer la pagination
  const indexOfLastPurchase = currentPage * purchasesPerPage;
  const indexOfFirstPurchase = indexOfLastPurchase - purchasesPerPage;
  const currentPurchases = purchases.slice(indexOfFirstPurchase, indexOfLastPurchase);
  const totalPurchases = purchases.length;
  const totalPages = Math.ceil(totalPurchases / purchasesPerPage);

  // Statistiques pour les cartes
  const stats = [
    {
      title: 'Total des Achats',
      value: allPurchases.reduce((total, purchase) => total + parseFloat(purchase.amount.replace(/[^0-9.-]+/g,'')), 0).toLocaleString('fr-FR') + ' DA',
      icon: ShoppingCartIcon,
      color: 'from-purple-600 to-blue-600'
    },
    {
      title: 'Achats en Attente',
      value: allPurchases.filter(purchase => purchase.status === 'ordered').length,
      icon: ClockIcon,
      color: 'from-yellow-600 to-amber-600'
    },
    {
      title: 'Fournisseurs',
      value: [...new Set(allPurchases.map(purchase => purchase.supplier))].length,
      icon: BuildingOffice2Icon,
      color: 'from-green-600 to-teal-600'
    }
  ];

  // Répartition par type d'achat
  const purchaseDistribution = [
    {
      name: 'Produits',
      count: allPurchases.filter(purchase => purchase.type === 'products').length,
      color: 'from-purple-600 to-blue-600'
    },
    {
      name: 'Équipement',
      count: allPurchases.filter(purchase => purchase.type === 'equipment').length,
      color: 'from-blue-600 to-cyan-600'
    },
    {
      name: 'Services',
      count: allPurchases.filter(purchase => purchase.type === 'services').length,
      color: 'from-green-600 to-teal-600'
    }
  ];

  // Onglets
  const tabs = [
    { id: 'all', name: 'Tous les Achats', icon: DocumentTextIcon },
    { id: 'received', name: 'Reçus', icon: CheckCircleIcon },
    { id: 'ordered', name: 'Commandés', icon: ClockIcon },
    { id: 'partial', name: 'Partiellement Reçus', icon: TruckIcon },
    { id: 'cancelled', name: 'Annulés', icon: ExclamationTriangleIcon },
  ];

  return (
    <>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white">Achats</h1>
            <p className="text-white/60">Gérez vos commandes fournisseurs</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleReceiveItems}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 transition-colors flex items-center space-x-2"
              title="Scanner pour réceptionner des produits"
            >
              <QrCodeIcon className="h-5 w-5" />
              <span>Réception</span>
            </button>
            
            <Link
              to="/purchases/new"
              className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Nouvel Achat</span>
            </Link>
          </div>
        </div>

        <Routes>
          <Route path="/" element={
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                        <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                      </div>
                      <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                        <stat.icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Tabs Navigation */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setCurrentPage(1);
                      }}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                        activeTab === tab.id
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <tab.icon className="h-5 w-5" />
                      <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Section Répartition par Type d'Achat retirée selon demande */}

              {/* Search and Filters */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  {/* Search */}
                  <div className="relative flex-1 max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder="Rechercher un achat..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Status Filter */}
                  <div className="flex items-center space-x-2">
                    <FunnelIcon className="h-5 w-5 text-white/50" />
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="all">Tous les statuts</option>
                      <option value="received">Reçus</option>
                      <option value="ordered">Commandés</option>
                      <option value="partial">Partiellement reçus</option>
                      <option value="cancelled">Annulés</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Purchases List */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
                {purchases.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-12 text-center">
                    <DocumentTextIcon className="h-16 w-16 text-white/30 mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">Aucun achat trouvé</h3>
                    <p className="text-white/60 mb-6">Aucun résultat ne correspond à vos critères de recherche.</p>
                    <Link
                      to="/purchases/new"
                      className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-2"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span>Créer un achat</span>
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-white/5">
                          <tr>
                            <th className="px-6 py-4 text-left text-white font-semibold">N° Achat</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Fournisseur</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Date</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Montant</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Type</th>
                            <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10">
                          {currentPurchases.map((purchase) => (
                            <motion.tr
                              key={purchase.id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="hover:bg-white/5 transition-colors"
                            >
                              <td className="px-6 py-4 text-white font-mono text-sm">{purchase.purchaseNumber}</td>
                              <td className="px-6 py-4 text-white font-medium">{purchase.supplier}</td>
                              <td className="px-6 py-4 text-white/80">{purchase.date}</td>
                              <td className="px-6 py-4 text-white font-medium">{purchase.amount}</td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  purchase.status === 'received'
                                    ? 'bg-green-500/20 text-green-400'
                                    : purchase.status === 'ordered'
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : purchase.status === 'partial'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-red-500/20 text-red-400'
                                }`}>
                                  {purchase.status === 'received' ? 'Reçu' :
                                   purchase.status === 'ordered' ? 'Commandé' :
                                   purchase.status === 'partial' ? 'Partiel' : 'Annulé'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  purchase.type === 'bon-commande'
                                    ? 'bg-purple-500/20 text-purple-400'
                                    : purchase.type === 'bon-reception'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-green-500/20 text-green-400'
                                }`}>
                                  {purchase.type === 'bon-commande' ? 'Bon de commande' :
                                   purchase.type === 'bon-reception' ? 'Bon de réception' : 'Facture d\'achat'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleView(purchase)}
                                    className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                    title="Voir les détails"
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDownloadPDF(purchase)}
                                    className="p-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors"
                                    title="Télécharger en PDF"
                                  >
                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleEdit(purchase)}
                                    className="p-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-colors"
                                    title="Modifier"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(purchase.id)}
                                    className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                    title="Supprimer"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                        <div className="text-sm text-white/60">
                          Affichage de {(currentPage - 1) * purchasesPerPage + 1} à {Math.min(currentPage * purchasesPerPage, totalPurchases)} sur {totalPurchases} achats
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Précédent
                          </button>
                          <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Suivant
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          } />

          {/* Routes pour la création et l'édition */}
          <Route path="/new" element={<PurchaseForm />} />
          <Route path="/:id" element={<PurchaseDetail />} />
          <Route path="/:id/edit" element={<PurchaseForm />} />
        </Routes>
      </div>

      {/* Scanner de code-barres modal - en dehors du composant Routes */}
      <BarcodeScanner 
        isOpen={isScannerOpen}
        onClose={closeScanner}
        onScan={handleScan}
        onError={handleScanError}
        mode="add"
        title="Scanner pour réceptionner des produits"
        continuous={true}
      />
    </>
  );
};

export default PurchasesPage; 